<template>
  <div class="form-group lm-editor" v-if="isVisible" :class="{ row : isOnModeLabelOnLeft }">
    <label
      :for="idComputed"
      :title="titleComputed"
      :class=" {'col-sm-2': isOnModeLabelOnLeft, 'col-form-label': isOnModeLabelOnLeft }"
      class="text-truncate"
    >
      {{label}}
      <small v-if="required">*</small>
    </label>
    <font-awesome-icon
      id="font-awesome-icon-edit"
      icon="edit"
      size="sm"
      class="clickable float-right mt-1"
      v-if="showEditButton"
      @click="$emit('edit-button-clicked')"
      />
    <editor-menu-bar
      :editor="editor"
      v-slot="{ commands, isActive }"
      :class="{ 'col-sm-10': isOnModeLabelOnLeft }"
    >
      <div class="menubar">
        <button
          tabindex="-1"
          class="btn btn-outline-secondary mr-1"
          :class="{ 'is-active': isActive.bold() }"
          @click="commands.bold"
          v-b-tooltip.hover
          title="Negrita (ctrl+b)"
          v-if="activeBold"
        >
          <font-awesome-icon icon="bold" />
        </button>

        <button
          tabindex="-1"
          class="btn btn-outline-secondary mr-1"
          :class="{ 'is-active': isActive.italic() }"
          @click="commands.italic"
          v-b-tooltip.hover
          title="Itálica (ctrl+i)"
          v-if="activeItalic"
        >
          <font-awesome-icon icon="italic" />
        </button>

        <button
          tabindex="-1"
          class="btn btn-outline-secondary mr-1"
          :class="{ 'is-active': isActive.strike() }"
          @click="commands.strike"
          v-b-tooltip.hover
          title="Tachado"
          v-if="activeStrike"
        >
          <font-awesome-icon icon="strikethrough" />
        </button>

        <button
          tabindex="-1"
          class="btn btn-outline-secondary mr-1"
          :class="{ 'is-active': isActive.underline() }"
          @click="commands.underline"
          v-b-tooltip.hover
          title="Subrayado"
          v-if="activeUnderline"
        >
          <font-awesome-icon icon="underline" />
        </button>

        <button
          tabindex="-1"
          class="btn btn-outline-secondary mr-1"
          :class="{ 'is-active': isActive.paragraph() }"
          @click="commands.paragraph"
          v-b-tooltip.hover
          title="Párrafo"
          v-if="activeParagraph"
        >
          <font-awesome-icon icon="paragraph" />
        </button>

        <editor-menu-bubble id="btn-link" v-if="activeLink" class="menububble btn btn-outline-secondary p-0 mr-1 " :editor="editor" @hide="hideLinkMenu" v-slot="{ commands, isActive, getMarkAttrs, menu }">
          <div
            class="menububble"
            :class="{ 'is-active': menu.isActive }"
            :style="`left: ${menu.left}px; bottom: ${menu.bottom}px;`"
          >

            <form class="menububble__form " v-if="linkMenuIsActive" @submit.prevent="setLinkUrl(commands.link, linkUrl)">
              <input class="menububble__input border-0 pl-2 mr-1" type="text" v-model="linkUrl" placeholder="https://" ref="linkInput" @keydown.esc="hideLinkMenu"/>
              <button class="menububble__button border-0 bg-white" @click="setLinkUrl(commands.link, null)" type="button">
                <font-awesome-icon icon="trash-alt" />
              </button>
            </form>

            <template v-else>
              <button
                tabindex="-1"
                class="btn btn-outline-secondary border-0 menububble__btn"
                @click="showLinkMenu(getMarkAttrs('link'))"
                v-b-tooltip.hover
                title="Enlace"
                :class="{ 'is-active': isActive.link() }"
              >
                <font-awesome-icon icon="link" />
              </button>
            </template>

          </div>
        </editor-menu-bubble>

        <button
          tabindex="-1"
          class="btn btn-outline-secondary mr-1"
          :class="{ 'is-active': isActive.heading({ level: 1 }) }"
          @click="commands.heading({ level: 1 })"
          v-b-tooltip.hover
          title="Título 1"
          v-if="activeH1"
        >H1</button>

        <button
          tabindex="-1"
          class="btn btn-outline-secondary mr-1"
          :class="{ 'is-active': isActive.heading({ level: 2 }) }"
          @click="commands.heading({ level: 2 })"
          v-b-tooltip.hover
          title="Título 2"
          v-if="activeH2"
        >H2</button>

        <button
          tabindex="-1"
          class="btn btn-outline-secondary mr-1"
          :class="{ 'is-active': isActive.heading({ level: 3 }) }"
          @click="commands.heading({ level: 3 })"
          v-b-tooltip.hover
          title="Título 3"
          v-if="activeH3"
        >H3</button>

        <button
          tabindex="-1"
          class="btn btn-outline-secondary mr-1"
          :class="{ 'is-active': isActive.heading({ level: 4 }) }"
          @click="commands.heading({ level: 4 })"
          v-b-tooltip.hover
          title="Título 4"
          v-if="activeH4"
        >H4</button>

        <button
          tabindex="-1"
          class="btn btn-outline-secondary mr-1"
          :class="{ 'is-active': isActive.heading({ level: 5 }) }"
          @click="commands.heading({ level: 5 })"
          v-b-tooltip.hover
          title="Título 5"
          v-if="activeH5"
        >H5</button>

        <button
          tabindex="-1"
          class="btn btn-outline-secondary mr-1"
          :class="{ 'is-active': isActive.heading({ level: 6 }) }"
          @click="commands.heading({ level: 6 })"
          v-b-tooltip.hover
          title="Título 6"
          v-if="activeH6"
        >H6</button>

        <button
          tabindex="-1"
          class="btn btn-outline-secondary mr-1"
          :class="{ 'is-active': isActive.bullet_list() }"
          @click="commands.bullet_list"
          v-b-tooltip.hover
          title="Viñetas"
          v-if="activeBulletList"
        >
          <font-awesome-icon icon="list" />
        </button>

        <button
          tabindex="-1"
          class="btn btn-outline-secondary mr-1"
          :class="{ 'is-active': isActive.ordered_list() }"
          @click="commands.ordered_list"
          v-b-tooltip.hover
          title="Lista"
          v-if="activeOrderedList"

        >
          <font-awesome-icon icon="list-ol" />
        </button>

        <button
          tabindex="-1"
          class="btn btn-outline-secondary mr-1"
          :class="{ 'is-active': isActive.blockquote() }"
          @click="commands.blockquote"
          v-b-tooltip.hover
          title="Cita"
          v-if="activeBlockquote"
        >
          <font-awesome-icon icon="quote-right" />
        </button>

        <button
          tabindex="-1"
          class="btn btn-outline-secondary mr-1"
          @click="commands.horizontal_rule"
          v-b-tooltip.hover
          title="Línea Horizontal"
          v-if="activeHR"
        >hr</button>

        <button
          tabindex="-1"
          class="btn btn-outline-secondary mr-1"
          @click="commands.undo"
          v-b-tooltip.hover
          title="Deshacer (ctrl+z)"
          v-if="activeUndo"
        >
          <font-awesome-icon icon="undo" />
        </button>

        <button
          tabindex="-1"
          class="btn btn-outline-secondary mr-1"
          @click="commands.redo"
          v-b-tooltip.hover
          title="Rehacer (ctrl+y)"
          v-if="activeRedo"
        >
          <font-awesome-icon icon="redo" />
        </button>

      </div>
    </editor-menu-bar>

    <editor-menu-bar
      :editor="editor"
      v-slot="{ commands }"
      :class="{ 'col-sm-10': isOnModeLabelOnLeft }"
    >
      <div class="menubar mt-1">
        <button
          tabindex="-1"
          class="btn btn-outline-secondary mr-1"
          @click="commands.createTable({rowsCount: 3, colsCount: 3, withHeaderRow: false })"
          v-b-tooltip.hover.bottom
          title="Insertar Tabla"
          v-if="activeTable"
        >
          <img src="../assets/images/icons/table.svg">
        </button>

        <button
          tabindex="-1"
          class="btn btn-outline-secondary mr-1"
          @click="commands.deleteTable()"
          v-b-tooltip.hover.bottom
          title="Eliminar Tabla"
          v-if="activeTable"
        >
          <img src="../assets/images/icons/delete_table.svg">
        </button>

        <button
          tabindex="-1"
          class="btn btn-outline-secondary mr-1"
          @click="commands.addColumnBefore()"
          v-b-tooltip.hover.bottom
          title="Agregar Columna Izq"
          v-if="activeTable"
        >
          <img src="../assets/images/icons/add_col_before.svg">
        </button>

        <button
          tabindex="-1"
          class="btn btn-outline-secondary mr-1"
          @click="commands.addColumnAfter()"
          v-b-tooltip.hover.bottom
          title="Agregar Columna Der"
          v-if="activeTable"
        >
          <img src="../assets/images/icons/add_col_after.svg">
        </button>

        <button
          tabindex="-1"
          class="btn btn-outline-secondary mr-1"
          @click="commands.deleteColumn()"
          v-b-tooltip.hover.bottom
          title="Borrar Columna"
          v-if="activeTable"
        >
          <img src="../assets/images/icons/delete_col.svg">
        </button>

        <button
          tabindex="-1"
          class="btn btn-outline-secondary mr-1"
          @click="commands.addRowBefore()"
          v-b-tooltip.hover.bottom
          title="Agregar Fila Arriba"
          v-if="activeTable"
        >
          <img src="../assets/images/icons/add_row_before.svg">
        </button>

        <button
          tabindex="-1"
          class="btn btn-outline-secondary mr-1"
          @click="commands.addRowAfter()"
          v-b-tooltip.hover.bottom
          title="Agregar Fila Abajo"
          v-if="activeTable"
        >
          <img src="../assets/images/icons/add_row_after.svg">
        </button>

        <button
          tabindex="-1"
          class="btn btn-outline-secondary mr-1"
          @click="commands.deleteRow()"
          v-b-tooltip.hover.bottom
          title="Borrar Fila"
          v-if="activeTable"
        >
          <img src="../assets/images/icons/delete_row.svg">
        </button>

        <button
          tabindex="-1"
          class="btn btn-outline-secondary mr-1"
          @click="commands.toggleCellMerge()"
          v-b-tooltip.hover.bottom
          title="Combinar Columnas"
          v-if="activeTable"
        >
          <img src="../assets/images/icons/combine_cells.svg">
        </button>
        <LmSuperPowerSelectV2
          ref="lmSuperPowerVariables"
          v-if="variablesOptions.length"
          class="w-50 mt-2"
          :options="variablesOptions"
          placeholder="Insertar variable"
          @input="insertVariable($event)"
        />
      </div>
    </editor-menu-bar>
    <editor-content
      class="form-control mt-2 editor-tiptap"
      :class="{'is-invalid': v.$error}"
      :editor="editor"
      :id="idComputed"
    />
    <p class="invalid-feedback" v-if="v.required === false">{{ $t('field_required')}}</p>
  </div>
</template>

<script>
import { Editor, EditorContent, EditorMenuBar, EditorMenuBubble } from 'tiptap'
import {
  Blockquote,
  HardBreak,
  Heading,
  HorizontalRule,
  OrderedList,
  BulletList,
  ListItem,
  TodoItem,
  TodoList,
  Bold,
  Italic,
  Link,
  Strike,
  Underline,
  History,
  Table,
  TableHeader,
  TableCell,
  TableRow
} from 'tiptap-extensions'
import lmInputMixin from '@/mixins/lmInputMixin.js'
import LmSuperPowerSelectV2 from '@/components/form/elements/LmSuperPowerSelectV2'

export default {
  mixins: [lmInputMixin],
  data () {
    return {
      html: '',
      once: 0,
      editor: new Editor({
        extensions: [
          new Blockquote(),
          new BulletList(),
          new HardBreak(),
          new Heading({ levels: [1, 2, 3, 4, 5, 6] }),
          new HorizontalRule(),
          new ListItem(),
          new OrderedList(),
          new TodoItem(),
          new TodoList(),
          new Link(),
          new Bold(),
          new Italic(),
          new Strike(),
          new Underline(),
          new History(),
          new Table({
            resizable: true
          }),
          new TableHeader(),
          new TableCell(),
          new TableRow()
        ],
        content: this.contentProps,
        onUpdate: ({ getHTML }) => {
          this.html = getHTML()
          // Por https://github.com/lumile/mesa-de-entradas/issues/503
          // nos aseguramos de quitar del string en value los potenciales tags <[/]p>
          // que existan
          this.html = this.html.replace(/<\/{0,}p>/g, '').trim().length === 0 ? '' : this.html
          this.$emit('onUpdate', this.html)
        }
      }),
      linkUrl: null,
      linkMenuIsActive: false
    }
  },
  components: {
    EditorMenuBar,
    EditorContent,
    EditorMenuBubble,
    LmSuperPowerSelectV2
  },
  props: {
    variablesOptions: {
      type: Array,
      default: () => []
    },
    actvieRedo: {
      type: Boolean,
      default: true
    },
    activeUnderline: {
      type: Boolean,
      default: true
    },
    activeBold: {
      type: Boolean,
      default: true
    },
    activeLink: {
      type: Boolean,
      default: true
    },
    activeItalic: {
      type: Boolean,
      default: true
    },
    activeStrike: {
      type: Boolean,
      default: true
    },
    activeParagraph: {
      type: Boolean,
      default: true
    },
    activeH1: {
      type: Boolean,
      default: true
    },
    activeH2: {
      type: Boolean,
      default: true
    },
    activeH3: {
      type: Boolean,
      default: true
    },
    activeH4: {
      type: Boolean,
      default: true
    },
    activeH5: {
      type: Boolean,
      default: true
    },
    activeH6: {
      type: Boolean,
      default: true
    },
    activeBulletList: {
      type: Boolean,
      default: true
    },
    activeOrderedList: {
      type: Boolean,
      default: true
    },
    activeBlockquote: {
      type: Boolean,
      default: true
    },
    activeHR: {
      type: Boolean,
      default: true
    },
    activeUndo: {
      type: Boolean,
      default: true
    },
    activeRedo: {
      type: Boolean,
      default: true
    },
    activeTable: {
      type: Boolean,
      default: true
    },
    contentProps: {
      type: String,
      default: ''
    }
  },
  watch: {
    contentProps () {
      if (this.contentProps === '') {
        this.once = 0
      } else {
        this.once++
        if (this.once === 1) {
          this.editor.setContent(this.contentProps)
        }
      }
    }
  },
  methods: {
    async insertVariable (variable) {
      if (variable?.value) {
        const transaction = this.editor.state.tr.insertText(`{{${variable?.value}}}`)
        this.editor.view.dispatch(transaction)
        await this.$nextTick()
        this.$refs.lmSuperPowerVariables.remove()
      }
    },
    showLinkMenu (attrs) {
      this.linkUrl = attrs.href
      this.linkMenuIsActive = true
      this.$nextTick(() => {
        this.$refs.linkInput.focus()
      })
    },
    hideLinkMenu () {
      this.linkUrl = null
      this.linkMenuIsActive = false
    },
    setLinkUrl (command, url) {
      command({ href: url })
      this.hideLinkMenu()
    }
  },
  beforeDestroy () {
    this.editor.destroy()
  }
}
</script>

<style lang="sass">
.editor-tiptap
  height: auto !important
  ul
    margin-left: 30px
    li
      list-style: disc !important
  table
    border-collapse: collapse
    table-layout: fixed
    width: 100%
    margin: 0
    overflow: hidden
    td, th
      min-width: 1em
      border: 2px solid #dddddd
      padding: 3px 5px
      vertical-align: top
      box-sizing: border-box
      position: relative
      > *
        margin-bottom: 0
    th
      font-weight: bold
      text-align: left
    .selectedCell:after
      z-index: 2
      position: absolute
      content: ""
      left: 0
      right: 0
      top: 0
      bottom: 0
      background: rgba(200, 200, 255, 0.4)
      pointer-events: none
    .column-resize-handle
      position: absolute
      right: -2px
      top: 0
      bottom: 0
      width: 4px
      z-index: 20
      background-color: #adf
      pointer-events: none

.menububble
  max-height: 35px !important
.menububble:hover
  background: #fff !important
  color: #333 !important
.menububble__form
  margin: 0px 0px
  padding: 1px
.menububble__input
  border-radius: 0px
.menububble__input:focus
  outline: none !important
.menububble__button
  margin: 0
  max-height: 32px
  max-width: 33px
.menububble__btn
  border-radius: 0 !important
.ProseMirror:focus
  outline: 0
.menubar
  button
    width: 40px
    height: 35px
    padding: 0
    img
      width: 14px
      height: 14px
</style>
